import React, { useState } from 'react';
import { Container, Form, Button, Alert, Card, Modal, Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import { KeyRound } from 'lucide-react';

const LoginForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantDomain } = useParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState(location?.state?.error || '');
  const [successMessage, setSuccessMessage] = useState(location?.state?.message || '');
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [resetPasswordSent, setResetPasswordSent] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  
  // Reset password states
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    email: '',
    verificationKey: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/${tenantDomain}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Login failed. Please check your credentials and try again.');
      }

      const data = await response.json();
      await login(data);
      
      // Store tenant domain
      localStorage.setItem('tenantDomain', tenantDomain);

      // Redirect to home page
      navigate(`/${tenantDomain}/home`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError('Please enter your email address');
      return;
    }
    
    setIsRequestingReset(true);
    setForgotPasswordError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/trigger-reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to send reset password email. Please try again.');
      }

      setResetPasswordSent(true);
      setResetPasswordForm({
        ...resetPasswordForm,
        email: forgotPasswordEmail
      });
      setTimeout(() => {
        setShowForgotPassword(false);
        setShowResetPassword(true);
      }, 2000);
    } catch (err) {
      setForgotPasswordError(err.message);
    } finally {
      setIsRequestingReset(false);
    }
  };

  const handleResetPasswordChange = (e) => {
    setResetPasswordForm({
      ...resetPasswordForm,
      [e.target.name]: e.target.value
    });
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetPasswordError('');

    // Validate passwords match
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      setResetPasswordError('Passwords do not match');
      return;
    }

    // Validate verification key is provided
    if (!resetPasswordForm.verificationKey.trim()) {
      setResetPasswordError('Verification key is required');
      return;
    }

    setIsResettingPassword(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: resetPasswordForm.email,
          verificationKey: resetPasswordForm.verificationKey,
          newPassword: resetPasswordForm.newPassword
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Error resetting password: ${errorData || response.status}`);
      }

      // Reset form and show success
      setResetPasswordForm({
        email: '',
        verificationKey: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowResetPassword(false);
      setSuccessMessage('Password has been reset successfully. You can now log in with your new password.');
    } catch (error) {
      setResetPasswordError(error.message || 'Failed to reset password. Please check your verification key and try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <Container className="py-5">
      <Card className="mx-auto" style={{ maxWidth: '400px' }}>
        <Card.Body>
          <Card.Title className="text-center mb-4">Login to {tenantDomain}</Card.Title>
          
          {error && (
            <Alert variant="danger">{error}</Alert>
          )}
          
          {successMessage && (
            <Alert variant="success">{successMessage}</Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
              />
            </Form.Group>

            <Button variant="primary" type="submit" className="w-100 mb-3">
              Login
            </Button>
            
            <div className="text-center">
              <Button 
                variant="link" 
                className="p-0 text-decoration-none" 
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotPasswordEmail(formData.email);
                }}
              >
                Forgot Password?
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Forgot Password Modal */}
      <Modal show={showForgotPassword} onHide={() => setShowForgotPassword(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetPasswordSent ? (
            <Alert variant="success">
              Password reset email sent! Redirecting to reset form...
            </Alert>
          ) : (
            <>
              {forgotPasswordError && (
                <Alert variant="danger">{forgotPasswordError}</Alert>
              )}
              <p>Enter your email address, and we'll send you a verification code to reset your password.</p>
              <Form onSubmit={handleForgotPasswordSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForgotPassword(false)} disabled={isRequestingReset}>
            Cancel
          </Button>
          {!resetPasswordSent && (
            <Button variant="primary" onClick={handleForgotPasswordSubmit} disabled={isRequestingReset}>
              {isRequestingReset ? (
                <>
                  <Spinner 
                    as="span" 
                    animation="border" 
                    size="sm" 
                    role="status" 
                    aria-hidden="true" 
                    className="me-2" 
                  />
                  Sending...
                </>
              ) : (
                <>
                  <KeyRound size={16} className="me-1" /> Send Reset Link
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      
      {/* Reset Password Modal */}
      <Modal show={showResetPassword} onHide={() => setShowResetPassword(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Your Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetPasswordError && (
            <Alert variant="danger">{resetPasswordError}</Alert>
          )}
          <p>Enter the verification code sent to your email and your new password.</p>
          <Form onSubmit={handleResetPasswordSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Verification Code</Form.Label>
              <Form.Control
                type="text"
                name="verificationKey"
                value={resetPasswordForm.verificationKey}
                onChange={handleResetPasswordChange}
                required
                placeholder="Enter verification code"
              />
              <Form.Text className="text-muted">
                Check your email for the verification code
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={resetPasswordForm.newPassword}
                onChange={handleResetPasswordChange}
                required
                placeholder="Enter new password"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={resetPasswordForm.confirmPassword}
                onChange={handleResetPasswordChange}
                required
                placeholder="Confirm new password"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetPassword(false)} disabled={isResettingPassword}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleResetPasswordSubmit} disabled={isResettingPassword}>
            {isResettingPassword ? (
              <>
                <Spinner 
                  as="span" 
                  animation="border" 
                  size="sm" 
                  role="status" 
                  aria-hidden="true" 
                  className="me-2" 
                />
                Resetting...
              </>
            ) : "Reset Password"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LoginForm;